import { useState, useEffect, useCallback, useRef } from 'react';
import { getDefaultTournamentData } from '../../utils/defaultTournamentData';
import { formatTimer } from '../../utils/viewHelpers';
import { useTimerWorker } from './useTimerWorker';
import { useTimerSync } from './useTimerSync';
import { useTournamentStructure } from './useTournamentStructure';
import { useTournamentTimerActions } from './useTournamentTimerActions';
import { useTournamentNavigation } from './useTournamentNavigation';
import timeSyncService from '../../services/TimeSyncService';
import { useNotification } from '../../contexts/NotificationContext';
import { useLocale } from '../../contexts/LocaleContext';

const timeStringToSeconds = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return 0;
  const parts = timeString.split(':').map(Number);
  return parts.length === 2 ? parts[0] * 60 + parts[1] : 0;
};

export const useLocalTournamentTimer = (initialData) => {
  const [tournamentState, setTournamentState] = useState(getDefaultTournamentData());
  const [isReadyEditing, setIsReadyEditing] = useState(false);

  // Destructure state
  const { id, name, details, structure, timer_state } = tournamentState || {};
  const { columns, levels: rows, events } = structure || {};
  // eslint-disable-next-line no-unused-vars
  const { state, rowIndex, remaining, hasTournamentStarted } = timer_state || {};

  // --- Sub-hooks ---
  const stateRef = useRef(tournamentState);
  useEffect(() => { stateRef.current = tournamentState; }, [tournamentState]);

  const { syncTimerToDB, syncStructureToDB, updateDetails, updateName } = useTimerSync(id);

  const structureActions = useTournamentStructure({
    stateRef,
    setTournamentState,
    setIsReadyEditing,
    tournamentId: id,
    syncStructureToDB,
    updateDetails,
    updateName
  });

  // Worker callbacks
  const handleTick = useCallback((remainingTime) => {
    setTournamentState(prev => {
      if (!prev) return prev;
      return { ...prev, timer_state: { ...prev.timer_state, remaining: remainingTime } };
    });
  }, []);

  // Ref for worker callback to access latest nextLevel closure
  const nextLevelRef = useRef(); // Initialize without value, will be set in useEffect
  const handleLevelComplete = useCallback((options) => {
    if (nextLevelRef.current) {
      nextLevelRef.current(options);
    }
  }, []);

  const { startWorker, stopWorker, resetWorker } = useTimerWorker(handleTick, handleLevelComplete);

  // Action and Navigation Hooks
  const timerActions = useTournamentTimerActions({
    stateRef,
    setTournamentState,
    syncTimerToDB,
    startWorker,
    stopWorker,
    resetWorker,
    timeStringToSeconds
  });

  const navigation = useTournamentNavigation({
    stateRef,
    setTournamentState,
    syncTimerToDB,
    startWorker,
    stopWorker,
    resetWorker,
    timeStringToSeconds
  });

  // --- Logic ---

  // Initialize state
  useEffect(() => {
    if (initialData) {
      const timerStateFromDb = initialData.timer_state || {};
      const rowIndexFromDb = timerStateFromDb.rowIndex ?? 0;
      let calculatedRemaining = timerStateFromDb.remaining ?? 0;

      // Recalculate remaining time for UI based on endsAt/pausedAt
      if (timerStateFromDb.state === 'play' && timerStateFromDb.endsAt) {
        const endsAtTime = new Date(timerStateFromDb.endsAt).getTime();
        calculatedRemaining = Math.max(0, Math.ceil((endsAtTime - timeSyncService.getNow()) / 1000));
      } else if (timerStateFromDb.state === 'pause' && timerStateFromDb.pausedAt !== undefined) {
        calculatedRemaining = timerStateFromDb.pausedAt;
      } else if (timerStateFromDb.state === 'stop') {
        // If the tournament is explicitly stopped, preserve the remaining time from DB (usually 0)
        calculatedRemaining = timerStateFromDb.remaining ?? 0;
      } else if (initialData.structure?.levels?.[rowIndexFromDb]?.time) {
        // Otherwise, fall back to the level's default time
        calculatedRemaining = timeStringToSeconds(initialData.structure.levels[rowIndexFromDb].time);
      }

      const mergedTournamentState = {
        ...getDefaultTournamentData(),
        ...initialData,
        structure: {
          ...getDefaultTournamentData().structure,
          ...initialData.structure,
          columns: initialData.structure?.columns?.map((col, index) => {
            if (col.id) return col;
            const label = String(col.label || `col-${index}`);
            const isRequired = label === 'level' || label === 'time';
            // Determine a stable-ish ID if missing
            const stableId = isRequired ? `col-${label}` : `col-${label.replace(/\s+/g, '-').toLowerCase()}-${label.length}`;
            return {
              ...col,
              id: stableId
            };
          }) || getDefaultTournamentData().structure.columns,
          levels: initialData.structure?.levels?.map(level => ({ ...level, type: level.type || 'level' })) || getDefaultTournamentData().structure.levels,
        },
        timer_state: {
          ...getDefaultTournamentData().timer_state,
          ...timerStateFromDb,
          rowIndex: rowIndexFromDb,
          remaining: calculatedRemaining,
        },
      };

      setTournamentState(mergedTournamentState);

      // Start local worker if playing
      if (mergedTournamentState.timer_state.state === 'play') {
        startWorker(calculatedRemaining);
      } else {
        // PING/WARM-UP REALTIME
        // Perform a silent update of the state to force Postgres to generate a WAL event.
        // This ensures the logical replication stream is fully awake before the user hits "Start".
        syncTimerToDB(mergedTournamentState.timer_state).catch(e => console.warn('Warmup ping failed', e));
      }
    } else {
      setTournamentState(prev => ({ ...prev, timer_state: { ...prev.timer_state, rowIndex: 0 } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, startWorker]);

  // Update ref for worker callback
  useEffect(() => { nextLevelRef.current = navigation.nextLevel; }, [navigation.nextLevel]);

  // Handle visibility change to recover from background throttling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTournamentState(prev => {
          if (!prev || prev.timer_state.state !== 'play' || !prev.timer_state.endsAt) return prev;

          const endsAtTime = new Date(prev.timer_state.endsAt).getTime();
          const newRemaining = Math.max(0, Math.ceil((endsAtTime - timeSyncService.getNow()) / 1000));

          if (Math.abs(newRemaining - prev.timer_state.remaining) > 1) {
            return { ...prev, timer_state: { ...prev.timer_state, remaining: newRemaining } };
          }
          return prev;
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const { showNotification } = useNotification();
  const { t } = useLocale();

  const handleSyncResult = useCallback((result) => {
    if (!result || !result.success) {
      const errorMsg = result?.error?.message || result?.error || 'Unknown error';
      showNotification(`${t('errors.updateFailed')}: ${errorMsg}`, 'error');
    }
  }, [showNotification, t]);

  const actions = {
    ...navigation,
    ...timerActions,
    resetTimer: () => {
      const currentState = stateRef.current;
      if (!currentState || !currentState.structure.levels) return;
      const initialLevelTime = timeStringToSeconds(currentState.structure.levels[0]?.time);
      timerActions.resetTimer(initialLevelTime);
    },
    forceStopTournament: async () => {
      const currentState = stateRef.current;
      if (!currentState) return;

      const newTimerState = {
        ...currentState.timer_state,
        state: 'stop',
        remaining: 0,
        rowIndex: currentState.structure.levels.length > 0 ? currentState.structure.levels.length - 1 : 0,
        hasTournamentStarted: true,
        endsAt: null,
        pausedAt: 0
      };

      setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
      const result = await syncTimerToDB(newTimerState);
      handleSyncResult(result);
      stopWorker();
    },
    scheduleTimer: async (scheduledTime) => {
      const currentState = stateRef.current;
      if (!currentState) return;

      const countdownSeconds = Math.round((scheduledTime.getTime() - new Date().getTime()) / 1000);
      if (countdownSeconds <= 0) return;

      const timeString = formatTimer(countdownSeconds);
      const { columns: currentColumns, levels: currentRows } = currentState.structure;
      const { name: currentName, details: currentDetails, id: currentId, timer_state: currentTimerState } = currentState;

      let newRows = [...currentRows];

      if (newRows[0]?.type === 'break') {
        newRows[0] = { ...newRows[0], time: timeString };
      } else {
        const newBreakRow = { id: `row-break-${Date.now()}`, type: "break", level: "break", time: timeString };
        currentColumns.forEach(col => { if (!newBreakRow.hasOwnProperty(col.label)) newBreakRow[col.label] = '—'; });
        newRows.unshift(newBreakRow);
      }

      let levelCounter = 0;
      newRows = newRows.map(r => (r.type === 'level' ? { ...r, level: ++levelCounter } : r));
      const newStructure = { ...currentState.structure, levels: newRows };
      const endsAt = new Date(timeSyncService.getNow() + countdownSeconds * 1000).toISOString();

      const newTimerState = {
        ...currentTimerState,
        state: 'play',
        remaining: countdownSeconds,
        rowIndex: 0,
        scheduledStartTime: scheduledTime.getTime(),
        playStartTime: null,
        hasTournamentStarted: true,
        pausedAt: null,
        endsAt: endsAt
      };

      if (currentId) {
        const result = await syncStructureToDB(currentName, currentDetails, newStructure, newTimerState);
        handleSyncResult(result);
      }

      setTournamentState(prev => ({
        ...prev,
        structure: newStructure,
        timer_state: newTimerState
      }));
      startWorker(countdownSeconds);
    },
    ...structureActions,
  };

  return {
    tournamentState,
    isReadyEditing,
    setIsReadyEditing,
    actions,
    timerState: state,
    remainingTime: remaining,
    rowIndex: rowIndex,
    hasTournamentStarted,
    tournamentName: name,
    tournamentDetails: details,
    columns,
    rows,
    events,
  };
};